import * as Sentry from "@sentry/react";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

export function debounce(callback, wait, immediate = false) {
  let timeout = null;

  return function () {
    const callNow = immediate && !timeout;
    const next = () => callback.apply(this, arguments);

    clearTimeout(timeout);
    timeout = setTimeout(next, wait);

    if (callNow) {
      next();
    }
  };
}

function createRequest(method, endpoint, params, headers, body) {
  const url = new URL(endpoint);

  const opts = {
    method,
  };

  if (params) {
    Object.keys(params).forEach((param) => {
      url.searchParams.set(param, params[param]);
    });
  }

  if (headers) {
    opts.headers = new Headers();
    Object.keys(headers).forEach((key) => {
      opts.headers.append(key, headers[key]);
    });
  }

  if (body) {
    opts.body = JSON.stringify(body);
  }
  return new Request(url, opts);
}

export class FetcherError extends Error {
  constructor(message) {
    super(message);
    this.name = "FetcherError";
  }
}

export function fetcher(method, endpoint, params, headers, body) {
  // Different domain depending on the environment.
  endpoint = "https://api." + window.location.host + "/" + endpoint;
  if (!method || !endpoint) return;
  const req = createRequest(method, endpoint, params, headers, body);
  return new Promise((resolve, reject) => {
    return fetch(req)
      .then(async (res) => {
        if (!res.ok) {
          let errorMessage = res.statusText;
          const contentType = res.headers.get("content-type");
          if (contentType && contentType.indexOf("json") !== -1) {
            const error = await res.json();
            if (error.errors && error.errors[0]?.detail) {
              errorMessage = error.errors[0]?.detail;
            }
          }
          throw new FetcherError(`${res.status} ${errorMessage}`);
        }
        // Successful DELETE requests to Drupal may return a 204 No Content
        // status code. Such is the case when deleting cart items. This is
        // valid and as there is no JSON to parse we can return early.
        else if (res.status == 204) {
          return resolve(true);
        }
        // Otherwise, assume all requests are formatted to return JSON, so
        // we should pass through the JSON data as an object.
        const result = await res.json();
        return resolve(result);
      })
      .catch((e) => {
        e.message = `${method} ${endpoint} ${e.message}`;
        console.error(e);
        Sentry.captureException(e, {
          extra: {
            method: method,
            endpoint: endpoint,
            params: params,
            headers: headers,
            body: body,
          },
        });
        return reject(new FetcherError(e.message));
      });
  });
}

export function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    let location = useLocation();
    let navigate = useNavigate();
    let params = useParams();
    return (
      <Component
        {...props}
        router={{ location, navigate, params }}
      />
    );
  }

  return ComponentWithRouterProp;
}
